import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Tag,
  Image,
  Descriptions,
  Divider,
  Space,
  Spin,
  message,
  Table
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { productService } from '../../services';
import { formatCurrency, formatDate } from '../../utils/helpers';

const { Title, Text, Paragraph } = Typography;

const ProductView = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stockHistory, setStockHistory] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    //console.log('ProductView - ID from URL:', id);
    if (id) {
      loadProduct();
    } else {
      //console.error('No product ID found in URL');
      message.error('Không tìm thấy ID sản phẩm');
      navigate('/products/list');
    }
  }, [id]);

  const loadProduct = async () => {
    if (!id) {
      message.error('Không tìm thấy ID sản phẩm');
      navigate('/products/list');
      return;
    }

    setLoading(true);
    try {
      //console.log('Loading product with ID:', id);
      const response = await productService.getById(id);
      //console.log('Product response:', response);
      
      // API trả về format {success: true, data: {...}}
      if (response && response.success && response.data) {
        setProduct(response.data);
        //console.log('✅ Product loaded successfully:', response.data);
      } else {
        //console.error('❌ Invalid response format:', response);
        message.error('Không thể tải thông tin sản phẩm');
        navigate('/products/list');
      }
    } catch (error) {
      //console.error('Error loading product:', error);
      message.error('Không thể tải thông tin sản phẩm');
      navigate('/products/list');
    } finally {
      setLoading(false);
    }
  };

  const loadStockHistory = async () => {
    try {
      // Assuming there's a stock history endpoint
      const response = await productService.getStockHistory(id);
      if (response.success) {
        setStockHistory(response.data || []);
      }
    } catch (error) {
      //console.error('Error loading stock history:', error);
    }
  };

  const stockHistoryColumns = [
    {
      title: 'Ngày',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'import' ? 'green' : 'red'}>
          {type === 'import' ? 'Nhập kho' : 'Xuất kho'}
        </Tag>
      ),
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity, record) => (
        <span style={{ color: record.type === 'import' ? '#52c41a' : '#ff4d4f' }}>
          {record.type === 'import' ? '+' : '-'}{quantity}
        </span>
      ),
    },
    {
      title: 'Lý do',
      dataIndex: 'reason',
      key: 'reason',
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Title level={3}>Không tìm thấy sản phẩm</Title>
        <Button onClick={() => navigate('/products/list')}>
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/products/list')}
            >
              Quay lại
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/products/edit/${product.id}`)}
            >
              Chỉnh sửa
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Product Image */}
        <Col xs={24} lg={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Image
                width="100%"
                height={300}
                src={product.image_url || (product.images && product.images.length > 0 ? product.images[0].image_url : null)}
                fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDMwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjBGMEYwIiBzdHJva2U9IiNEOUQ5RDkiLz4KPHBhdGggZD0iTTEwMCAxMDBIMTUwVjE1MEgxMDBWMTAwWk0yMDAgMjAwSDUwVjE1MEwxMDAgMTAwTDE1MCAxNTBMMjAwIDEwMEwyNTAgMTUwVjIwMFoiIGZpbGw9IiNCRkJGQkYiLz4KPHR4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5OTk5OTkiPk5vIEltYWdlPC90eHQ+Cjwvc3ZnPgo="
                preview={true}
                style={{ 
                  objectFit: 'cover', 
                  borderRadius: 8,
                  cursor: 'pointer',
                  border: '2px solid #d9d9d9'
                }}
              />
            </div>
            
            {/* Additional Images */}
            {product.images && product.images.length > 1 && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Hình ảnh khác:</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {product.images.slice(1).map((image, index) => (
                    <Image
                      key={image.id || index}
                      width={60}
                      height={60}
                      src={image.image_url}
                      style={{ 
                        objectFit: 'cover', 
                        borderRadius: 4,
                        border: '1px solid #d9d9d9'
                      }}
                      preview={true}
                    />
                  ))}
                </div>
              </div>
            )}
          </Card>
        </Col>

        {/* Product Info */}
        <Col xs={24} lg={16}>
          <Card>
            <Title level={2}>{product.name}</Title>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Space direction="vertical" size="small">
                  {product.is_featured && <Tag color="gold">Nổi bật</Tag>}
                  {product.is_new && <Tag color="green">Mới</Tag>}
                  {product.is_best_seller && <Tag color="red">Bán chạy</Tag>}
                  <Tag color={product.is_active ? 'green' : 'red'}>
                    {product.is_active ? 'Hoạt động' : 'Tạm dừng'}
                  </Tag>
                  {product.rating && product.rating.review_count > 0 && (
                    <Tag color="blue">
                      ⭐ {parseFloat(product.rating.avg_rating).toFixed(1)} ({product.rating.review_count} đánh giá)
                    </Tag>
                  )}
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" align="end">
                  {product.discount_price && parseFloat(product.discount_price) < parseFloat(product.price) ? (
                    <>
                      <Text delete style={{ fontSize: 16, color: '#999' }}>
                        {formatCurrency(product.price)}
                      </Text>
                      <Text strong style={{ fontSize: 24, color: '#ff4d4f' }}>
                        {formatCurrency(product.discount_price)}
                      </Text>
                      <Tag color="red">Khuyến mãi</Tag>
                    </>
                  ) : (
                    <Text strong style={{ fontSize: 24, color: '#1890ff' }}>
                      {formatCurrency(product.price)}
                    </Text>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        {/* Product Details */}
        <Col xs={24} lg={12}>
          <Card title="Thông tin chi tiết">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="ID">
                {product.id}
              </Descriptions.Item>
              <Descriptions.Item label="Danh mục">
                {product.category_name || 'Chưa phân loại'}
              </Descriptions.Item>
              <Descriptions.Item label="Giá bán">
                {formatCurrency(product.price)}
              </Descriptions.Item>
              {product.discount_price && parseFloat(product.discount_price) < parseFloat(product.price) && (
                <>
                  <Descriptions.Item label="Giá khuyến mãi">
                    <Text style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                      {formatCurrency(product.discount_price)}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Thời gian KM">
                    {product.promotion_start && product.promotion_end ? (
                      <span>
                        {formatDate(product.promotion_start)} - {formatDate(product.promotion_end)}
                      </span>
                    ) : 'Không giới hạn'}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Số biến thể">
                <span style={{ color: '#1890ff' }}>
                  {product.variants ? product.variants.length : 0} biến thể
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                <Tag color={product.is_active ? 'green' : 'red'}>
                  {product.is_active ? 'Hoạt động' : 'Tạm dừng'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Ngày tạo">
                {formatDate(product.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Cập nhật lần cuối">
                {formatDate(product.updated_at)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Product Description */}
        <Col xs={24} lg={12}>
          <Card title="Mô tả sản phẩm">
            {product.description ? (
              <Paragraph style={{ 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word',
                lineHeight: '1.6'
              }}>
                {product.description}
              </Paragraph>
            ) : (
              <Text type="secondary">Chưa có mô tả</Text>
            )}
            
            {product.notes && (
              <>
                <Divider />
                <Title level={5}>Ghi chú:</Title>
                <Paragraph style={{ 
                  whiteSpace: 'pre-wrap', 
                  wordBreak: 'break-word',
                  lineHeight: '1.6'
                }}>
                  {product.notes}
                </Paragraph>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Variants and Attributes */}
      {(product.variants && product.variants.length > 0) && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Card title="Biến thể sản phẩm">
              <Table
                columns={[
                  {
                    title: 'SKU',
                    dataIndex: 'sku',
                    key: 'sku',
                  },
                  {
                    title: 'Giá',
                    dataIndex: 'price',
                    key: 'price',
                    render: (price) => formatCurrency(price),
                  },
                  {
                    title: 'Cảnh báo tồn thấp',
                    dataIndex: 'low_stock_alert',
                    key: 'low_stock_alert',
                  },
                  {
                    title: 'Thuộc tính',
                    key: 'attributes',
                    render: (_, record) => {
                      const variantAttrs = product.variant_attributes?.filter(
                        attr => attr.variant_id === record.id
                      );
                      return (
                        <div>
                          {variantAttrs?.map((attr, index) => (
                            <Tag key={index} color="blue">
                              {attr.attribute_name}: {attr.attribute_value}
                            </Tag>
                          ))}
                        </div>
                      );
                    },
                  },
                ]}
                dataSource={product.variants}
                rowKey="id"
                size="small"
                pagination={false}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Product Attributes */}
      {product.attributes && product.attributes.length > 0 && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Card title="Thuộc tính sản phẩm">
              <div>
                {Object.entries(
                  product.attributes.reduce((acc, attr) => {
                    if (!acc[attr.attribute_name]) {
                      acc[attr.attribute_name] = [];
                    }
                    acc[attr.attribute_name].push(attr.attribute_value);
                    return acc;
                  }, {})
                ).map(([attrName, values]) => (
                  <div key={attrName} style={{ marginBottom: 16 }}>
                    <Text strong>{attrName}: </Text>
                    {values.map((value, index) => (
                      <Tag key={index} color="geekblue" style={{ margin: 4 }}>
                        {value}
                      </Tag>
                    ))}
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Stock History */}
      {stockHistory.length > 0 && (
        <Row style={{ marginTop: 24 }}>
          <Col span={24}>
            <Card title="Lịch sử tồn kho">
              <Table
                columns={stockHistoryColumns}
                dataSource={stockHistory}
                rowKey="id"
                size="small"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false
                }}
              />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ProductView;
